import { FC, useMemo, useState } from "react";
import { Button, Column, DataTable, Hyperlink } from "@racoon/ui";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useListTruckTypesQuery } from "~/shared/api/truck/truck-type.api";
import type { BlacklistDraftRow } from "../hooks/use-blacklist-draft";
import { BlacklistModal } from "./blacklist.modal";
import { formatContractDay } from "../model/contract-label";

type Props = {
  rows: BlacklistDraftRow[];
  isLoading?: boolean;
  serviceProviderId?: number;
  onAdd: (truckTypeId: bigint, truckTypeName: string, reason: string) => void;
  onRemove: (key: string) => void;
};

/**
 * The pickup location's blacklist: truck types that must not be sent here.
 *
 * Edits are staged — they reach the server when the modal is saved — so this
 * component only reports changes upward and never calls the API itself.
 */
export const BlacklistBlock: FC<Props> = ({
  rows,
  isLoading,
  serviceProviderId,
  onAdd,
  onRemove,
}) => {
  const t = useT();
  const dateLocale = useDateFnsLocale();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<BlacklistDraftRow | null>(null);

  const openRow = useMemo(
    () => (editingRow ? (rows.find((row) => row.key === editingRow.key) ?? null) : null),
    [rows, editingRow],
  );

  const { data: truckTypesResponse } = useListTruckTypesQuery(
    { ownerOrganizationIds: serviceProviderId ? [BigInt(serviceProviderId)] : [] },
    { skip: !serviceProviderId },
  );

  // A truck type can appear on the list only once — the backend enforces the
  // pair's uniqueness, so offering an already-listed type would only produce a
  // rejected save.
  const availableTruckTypes = useMemo(() => {
    const listed = new Set(rows.map((row) => String(row.truckTypeId)));
    return (truckTypesResponse?.customTruckTypes ?? []).filter(
      (type: ConnectProto.CustomTruckType) => !listed.has(String(type.id)),
    );
  }, [rows, truckTypesResponse]);

  const columns: Column<BlacklistDraftRow>[] = useMemo(
    () => [
      {
        id: "truckType",
        header: t(Namespace.PickupLocation, "blacklist.truckType"),
        sortable: true,
        width: "33.33%",
        accessor: (row) => row.truckTypeName,
        // The truck type doubles as the row's handle: pressing it opens the
        // entry, which is where it can be edited or removed.
        cell: (info) => (
          <Hyperlink
            onOpen={() => {
              setEditingRow(info.row.original);
            }}
          >
            {info.getValue() as string}
          </Hyperlink>
        ),
      },
      {
        id: "reason",
        header: t(Namespace.PickupLocation, "blacklist.reason"),
        sortable: true,
        width: "33.33%",
        accessor: (row) => row.reason || "—",
      },
      {
        id: "createdAt",
        header: t(Namespace.PickupLocation, "blacklist.addedAt"),
        sortable: true,
        width: "33.34%",
        // A staged row has no date yet: it gets one when the modal is saved.
        accessor: (row) =>
          formatContractDay(row.createdAt, dateLocale) ??
          t(Namespace.PickupLocation, "blacklist.unsaved"),
      },
    ],
    [t, dateLocale],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
      {/* Shaped like "Создать контейнерную группу" on the container groups tab —
          a ghost button that fills on hover rather than shifting its text
          colour. `slate-200` is #E2E8F0. */}
      <Button
        type="button"
        variant="ghost"
        className="self-start text-brand-blue hover:bg-slate-200"
        onClick={() => setIsAddModalOpen(true)}
        disabled={!serviceProviderId}
      >
        {t(Namespace.PickupLocation, "blacklist.addTruckType")}
      </Button>

      <div className="min-h-0 flex-1">
        <DataTable<BlacklistDraftRow>
          data={rows}
          columns={columns}
          loading={isLoading}
          rowKey={(row) => row.key}
          empty={
            <div className="p-8 text-center text-muted-foreground">
              {t(Namespace.PickupLocation, "blacklist.empty")}
            </div>
          }
        />
      </div>

      <BlacklistModal
        isOpen={isAddModalOpen || openRow !== null}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingRow(null);
        }}
        truckTypes={availableTruckTypes}
        companyId={serviceProviderId ?? undefined}
        onAdd={onAdd}
        row={openRow}
        onRemove={onRemove}
      />
    </div>
  );
};
